import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  Alert,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { monitoringService, LogLevel, LogFile } from '@/services/monitoringService';
import LiveLogsCard from './LiveLogsCard';
import LogSearchCard from './LogSearchCard';

const LogsTab: React.FC = () => {
  const [logLevel, setLogLevel] = useState<LogLevel | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>('info');
  const [changingLevel, setChangingLevel] = useState(false);
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    loadLogLevel();
    loadLogFiles();
  }, []);

  const loadLogLevel = async () => {
    try {
      const level = await monitoringService.getLogLevel();
      setLogLevel(level);
      setSelectedLevel(level.current);
    } catch (error) {
      toast.error('Failed to load log level');
    }
  };

  const loadLogFiles = async () => {
    setLoadingFiles(true);
    try {
      const files = await monitoringService.listLogFiles();
      setLogFiles(files);
    } catch (error) {
      toast.error('Failed to load log files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleChangeLogLevel = async () => {
    setChangingLevel(true);
    try {
      const result = await monitoringService.setLogLevel(selectedLevel);
      if (result.success) {
        toast.success(result.message);
        setLogLevel({ ...logLevel!, current: result.level });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to change log level');
    } finally {
      setChangingLevel(false);
    }
  };

  const handleDownload = (filename: string) => {
    const url = monitoringService.getDownloadUrl(filename);
    window.open(url, '_blank');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Logs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        System log management and monitoring
      </Typography>

      {/* Log Level Control */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Log Level Control
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Changes take effect immediately but do not persist across server restarts.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Log Level</InputLabel>
              <Select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                label="Log Level"
                size="small"
              >
                {logLevel?.available.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level.toUpperCase()}
                    {level === logLevel.current && ' (current)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleChangeLogLevel}
              disabled={changingLevel || selectedLevel === logLevel?.current}
              startIcon={changingLevel ? <CircularProgress size={16} /> : <CheckIcon />}
            >
              Apply
            </Button>
            {logLevel && (
              <Chip
                label={`Current: ${logLevel.current.toUpperCase()}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Log Search */}
      <LogSearchCard
        logFiles={logFiles}
        availableLevels={logLevel?.available || []}
        formatFileSize={formatFileSize}
      />

      {/* Live Logs */}
      <LiveLogsCard availableLevels={logLevel?.available || []} />

      {/* Log Files */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Log Files</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={loadLogFiles}
              startIcon={loadingFiles ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={loadingFiles}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logFiles.map((file) => (
                  <TableRow key={file.name} hover>
                    <TableCell>{file.name}</TableCell>
                    <TableCell align="right">{formatFileSize(file.size)}</TableCell>
                    <TableCell>{formatDate(file.modified)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(file.name)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {logFiles.length === 0 && !loadingFiles && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No log files found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LogsTab;
